import React, { useState, useRef } from 'react';
import {
    Box,
    Typography,
    Paper,
    Divider,
    Button,
    Grid,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Alert,
    CircularProgress,
    TextField,
} from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import {
    getFirestore,
    collection,
    query,
    where,
    getDocs,
    doc,
    getDoc,
    Timestamp,
} from 'firebase/firestore';
import { toast } from 'react-toastify';
import Fiananceheader from './Fiananceheader';
import { useReactToPrint } from 'react-to-print';

const db = getFirestore();

function Loanreport() {
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const [loans, setLoans] = useState([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [reportGenerated, setReportGenerated] = useState(false);
    const [error, setError] = useState('');
    const [totalLoanAmount, setTotalLoanAmount] = useState(0); // New state for total

    const printRef = useRef();

    const handleGetReport = async () => {
        setError('');
        if (!fromDate || !toDate) {
            setError('Please select both From Date and To Date.');
            return;
        }

        const fromTimestamp = Timestamp.fromDate(new Date(fromDate));
        const toTimestamp = Timestamp.fromDate(new Date(`${toDate}T23:59:59`));

        setLoading(true);
        try {
            const loansQuery = query(
                collection(db, 'loans'),
                where('loandate', '>=', fromTimestamp),
                where('loandate', '<=', toTimestamp)
            );

            const loansSnapshot = await getDocs(loansQuery);
            const loansList = loansSnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
            }));

            // Fetch customer details and calculate total amount
            let total = 0;
            const loansWithCustomers = await Promise.all(
                loansList.map(async (loan) => {
                    const customerRef = doc(db, 'fianancecustomers', loan.custid);
                    const customerSnap = await getDoc(customerRef);
                    const customerData = customerSnap.exists()
                        ? customerSnap.data()
                        : { name: 'Unknown', mobile: 'N/A' };
                    
                    // Add loanamount to the total
                    total += loan.loanamount || 0;

                    return {
                        ...loan,
                        customerName: customerData.name,
                        customerMobile: customerData.mobile,
                    };
                })
            );

            setLoans(loansWithCustomers);
            setTotalLoanAmount(total); // Set the calculated total
            setReportGenerated(true);
        } catch (err) {
            console.error('Error fetching report:', err);
            toast.error('Failed to generate report.');
            setError('Failed to fetch data. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handlePrint = useReactToPrint({
        content: () => printRef.current,
        documentTitle: `Loan Report from ${new Date(fromDate).toLocaleDateString()} to ${new Date(toDate).toLocaleDateString()}`,
        pageStyle: `
            @page {
                size: A4;
                margin: 20mm;
            }
            body {
                font-family: Arial, sans-serif;
            }
            h4, h6 {
                text-align: center;
            }
            table {
                width: 100%;
                border-collapse: collapse;
                margin-top: 15px;
            }
            th, td {
                border: 1px solid #ddd;
                padding: 8px;
                text-align: left;
            }
            th {
                background-color: #f2f2f2;
            }
            .no-print {
                display: none;
            }
        `
    });

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    return (
        <Box p={3}>
            <Fiananceheader />
            <Paper sx={{ p: 4, borderRadius: '5px' }}>
                <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, textAlign: 'center', color: '#1976d2', mb: 3 }}>
                    📊 Loan Report
                </Typography>
                <Divider sx={{ mb: 3 }} />

                <Grid container spacing={2} mb={3}>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            fullWidth
                            size="small"
                            label="या तारखेपासून "
                            type="date"
                            value={fromDate}
                            onChange={(e) => setFromDate(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            fullWidth
                            size="small"
                            label="या तारखेपर्यंत"
                            type="date"
                            value={toDate}
                            onChange={(e) => setToDate(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <Button
                            fullWidth
                            variant="contained"
                            onClick={handleGetReport}
                            disabled={loading}
                        >
                            {loading ? <CircularProgress size={24} /> : 'Get Report'}
                        </Button>
                    </Grid>
                </Grid>

                {error && (
                    <Alert severity="error" sx={{ mb: 3 }}>
                        {error}
                    </Alert>
                )}

                {reportGenerated && loans.length === 0 && !loading && (
                    <Alert severity="info" sx={{ mb: 3 }}>
                        No loans found for the selected date range.
                    </Alert>
                )}
                
                {loans.length > 0 && (
                    <Box sx={{ mt: 3, textAlign: 'right' }} className="no-print">
                        <Button
                            variant="outlined"
                            onClick={handlePrint}
                            startIcon={<PrintIcon />}
                        >
                            Print Report
                        </Button>
                    </Box>
                )}

                <div ref={printRef} style={{ padding: '16px' }}>
                    {loans.length > 0 && (
                        <>
                            <Typography variant="h6" align="center" sx={{ display: 'none', '@media print': { display: 'block' } }}>
                                Loan Report from {new Date(fromDate).toLocaleDateString()} to {new Date(toDate).toLocaleDateString()}
                            </Typography>
                            <TableContainer>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Sr. No.</TableCell>
                                             <TableCell>कर्जदाराचे नाव </TableCell>
                                            <TableCell>मोबाईल </TableCell>
                                            <TableCell>कर्जाची रक्कम </TableCell>
                                            <TableCell>हातात दिलेली रक्कम </TableCell>
                                            <TableCell>कर्ज तारीख </TableCell>
                                            <TableCell>स्टेटस </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {loans
                                            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                            .map((loan, index) => (
                                                <TableRow key={loan.id}>
                                                    <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                                                    <TableCell>{loan.customerName}</TableCell>
                                                    <TableCell>{loan.customerMobile}</TableCell>
                                                    <TableCell>₹{loan.loanamount}</TableCell>
                                                    <TableCell>₹{loan.actualamount}</TableCell>
                                                    <TableCell>
                                                        {loan.loandate && new Date(loan.loandate.seconds * 1000).toLocaleDateString()}
                                                    </TableCell>
                                                    <TableCell>{loan.status}</TableCell>
                                                </TableRow>
                                            ))}
                                        {/* Total Row */}
                                        <TableRow>
                                            <TableCell colSpan={3} align="right">
                                                <Typography variant="span" fontWeight="bold">टोटल कर्जाची रक्कम :</Typography>
                                            </TableCell>
                                            <TableCell colSpan={4}>
                                                <Typography variant="span" fontWeight="bold">₹{totalLoanAmount.toLocaleString('en-IN')}</Typography>
                                            </TableCell>
                                        </TableRow>
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            <TablePagination
                                rowsPerPageOptions={[5, 10, 25]}
                                component="div"
                                count={loans.length}
                                rowsPerPage={rowsPerPage}
                                page={page}
                                onPageChange={handleChangePage}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                                className="no-print"
                            />
                        </>
                    )}
                </div>
            </Paper>
        </Box>
    );
}

export default Loanreport;